import React from 'react'
import { AlertCircle, AlertTriangle, Info, CheckCircle, X } from 'lucide-react'
import { cn } from '../../../utils/styles'
import type { ValidationMessagesProps, ValidationResult } from '../types'

export function ValidationMessages({
  errors,
  warnings = [],
  position = 'bottom',
  className,
  customMessages = {}
}: ValidationMessagesProps) {
  const hasErrors = errors.length > 0
  const hasWarnings = warnings.length > 0

  if (!hasErrors && !hasWarnings) {
    return null
  }

  const positionClasses = {
    top: 'order-first',
    bottom: 'order-last',
    inline: ''
  }

  return (
    <div className={cn('space-y-2', positionClasses[position], className)}>
      {/* Error messages */}
      {hasErrors && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <ValidationMessage
              key={`error-${index}`}
              result={error}
              type="error"
              customMessages={customMessages}
            />
          ))}
        </div>
      )}

      {/* Warning messages */}
      {hasWarnings && (
        <div className="space-y-1">
          {warnings.map((warning, index) => (
            <ValidationMessage
              key={`warning-${index}`}
              result={warning}
              type="warning"
              customMessages={customMessages}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Individual validation message
interface ValidationMessageProps {
  result: ValidationResult
  type: 'error' | 'warning' | 'info' | 'success'
  customMessages?: Record<string, string>
  dismissible?: boolean
  onDismiss?: () => void
}

function ValidationMessage({
  result,
  type,
  customMessages = {},
  dismissible = false,
  onDismiss
}: ValidationMessageProps) {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-4 h-4" />
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />
      case 'success':
        return <CheckCircle className="w-4 h-4" />
      case 'info':
      default:
        return <Info className="w-4 h-4" />
    }
  }

  const getStyles = () => {
    const baseStyles = 'flex items-start space-x-2 p-3 rounded-md text-sm border'

    switch (type) {
      case 'error':
        return cn(baseStyles, 'bg-red-50 border-red-200 text-red-700')
      case 'warning':
        return cn(baseStyles, 'bg-yellow-50 border-yellow-200 text-yellow-700')
      case 'success':
        return cn(baseStyles, 'bg-green-50 border-green-200 text-green-700')
      case 'info':
      default:
        return cn(baseStyles, 'bg-blue-50 border-blue-200 text-blue-700')
    }
  }

  const getIconStyles = () => {
    switch (type) {
      case 'error':
        return 'text-red-500'
      case 'warning':
        return 'text-yellow-500'
      case 'success':
        return 'text-green-500'
      case 'info':
      default:
        return 'text-blue-500'
    }
  }

  // Get messages to display
  const messages = type === 'error' ? result.errors : result.warnings || []

  if (messages.length === 0) {
    return null
  }

  return (
    <div className={getStyles()}>
      <div className={cn('flex-shrink-0 mt-0.5', getIconStyles())}>
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        {messages.map((message, index) => (
          <div key={index} className="flex items-start justify-between">
            <p className="flex-1">
              {customMessages[message] || message}
            </p>

            {dismissible && messages.length === 1 && onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="flex-shrink-0 ml-2 p-0.5 rounded hover:bg-black hover:bg-opacity-10 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {/* Show file name if available */}
        {result.file && messages.length > 1 && (
          <p className="text-xs opacity-75 mt-1">
            File: {result.file.name}
          </p>
        )}
      </div>
    </div>
  )
}

// Compact version for inline display
export interface ValidationMessageCompactProps {
  message: string
  type?: 'error' | 'warning' | 'info' | 'success'
  className?: string
}

export function ValidationMessageCompact({
  message,
  type = 'error',
  className
}: ValidationMessageCompactProps) {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-3 h-3" />
      case 'warning':
        return <AlertTriangle className="w-3 h-3" />
      case 'success':
        return <CheckCircle className="w-3 h-3" />
      case 'info':
      default:
        return <Info className="w-3 h-3" />
    }
  }

  const getTextColor = () => {
    switch (type) {
      case 'error':
        return 'text-red-600'
      case 'warning':
        return 'text-yellow-600'
      case 'success':
        return 'text-green-600'
      case 'info':
      default:
        return 'text-blue-600'
    }
  }

  return (
    <div className={cn('flex items-center space-x-1 text-xs', getTextColor(), className)}>
      {getIcon()}
      <span>{message}</span>
    </div>
  )
}

// Summary component for multiple validation results
export interface ValidationSummaryProps {
  results: ValidationResult[]
  className?: string
  showDetails?: boolean
  onToggleDetails?: () => void
}

export function ValidationSummary({
  results,
  className,
  showDetails = false,
  onToggleDetails
}: ValidationSummaryProps) {
  const errorCount = results.filter(r => !r.isValid).length
  const warningCount = results.filter(r => r.warnings.length > 0).length
  const totalFiles = results.length

  if (results.length === 0) {
    return null
  }

  const hasErrors = errorCount > 0
  const hasWarnings = warningCount > 0

  return (
    <div className={cn('space-y-2', className)}>
      {/* Summary */}
      <div
        className={cn(
          'flex items-center justify-between p-3 rounded-md border cursor-pointer',
          {
            'bg-red-50 border-red-200': hasErrors,
            'bg-yellow-50 border-yellow-200': hasWarnings && !hasErrors,
            'bg-green-50 border-green-200': !hasErrors && !hasWarnings
          }
        )}
        onClick={onToggleDetails}
      >
        <div className="flex items-center space-x-2">
          {hasErrors ? (
            <AlertCircle className="w-5 h-5 text-red-500" />
          ) : hasWarnings ? (
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-500" />
          )}

          <div>
            <p className={cn(
              'text-sm font-medium',
              {
                'text-red-700': hasErrors,
                'text-yellow-700': hasWarnings && !hasErrors,
                'text-green-700': !hasErrors && !hasWarnings
              }
            )}>
              {hasErrors
                ? `${errorCount} file${errorCount !== 1 ? 's' : ''} failed validation`
                : hasWarnings
                ? `${warningCount} file${warningCount !== 1 ? 's' : ''} with warnings`
                : `All ${totalFiles} files are valid`
              }
            </p>

            {(hasErrors || hasWarnings) && (
              <p className="text-xs text-gray-600">
                Click to {showDetails ? 'hide' : 'show'} details
              </p>
            )}
          </div>
        </div>

        <div className={cn(
          'transform transition-transform',
          showDetails ? 'rotate-180' : ''
        )}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Details */}
      {showDetails && (hasErrors || hasWarnings) && (
        <div className="space-y-2 pl-4">
          <ValidationMessages
            errors={results.filter(r => !r.isValid)}
            warnings={results.filter(r => r.warnings.length > 0)}
            position="inline"
          />
        </div>
      )}
    </div>
  )
}

// Inline validation for individual files
export interface InlineValidationProps {
  file: File
  isValid: boolean
  errors?: string[]
  warnings?: string[]
  className?: string
}

export function InlineValidation({
  file,
  isValid,
  errors = [],
  warnings = [],
  className
}: InlineValidationProps) {
  if (isValid && warnings.length === 0) {
    return (
      <div className={cn('flex items-center space-x-1 text-xs text-green-600', className)}>
        <CheckCircle className="w-3 h-3" />
        <span>Valid</span>
      </div>
    )
  }

  const hasErrors = errors.length > 0
  const hasWarnings = warnings.length > 0

  return (
    <div className={cn('space-y-1', className)}>
      {hasErrors && (
        <div className="space-y-0.5">
          {errors.map((error, index) => (
            <ValidationMessageCompact
              key={`error-${index}`}
              message={error}
              type="error"
            />
          ))}
        </div>
      )}

      {hasWarnings && (
        <div className="space-y-0.5">
          {warnings.map((warning, index) => (
            <ValidationMessageCompact
              key={`warning-${index}`}
              message={warning}
              type="warning"
            />
          ))}
        </div>
      )}
    </div>
  )
}